import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { FormikProps } from 'formik'
import { FilterMatchMode, FilterOperator } from 'primereact/api'
import { Column } from 'primereact/column'
import { DataTableFilterMeta } from 'primereact/datatable'
import { Message } from 'primereact/message'

import { SearchField } from '../../../../../../../components/SearchField'
import {
  BooleanBodyTemplate,
  BooleanFilterTemplate,
  DecimalBodyTemplate,
  DecimalFilterTemplate,
  PackagingIdentifierBodyTemplate,
  PackagingIdentifierFilterTemplate,
  PackagingTypeBodyTemplate,
  PackagingTypeFilterTemplate,
  PropertyIdentifierBodyTemplate,
  PropertyIdentifierFilterTemplate,
  WeightUnitBodyTemplate,
} from '../../../../../../../components/tableTemplates'
import { DeliveryForecastDespatchAdvicePackagingMaterial } from '../../../../../../../data/models'
import { ConfigurableTable } from '../../../../../../../utils/wrapper'
import { useAllPackagingMaterial } from '../../../../../../selfservice/master-data-management/packaging-data/data'
import { TableConfig } from '../../../../../../ui-config/data'
import { FormValues } from '../FormValues'

const defaultFilters: DataTableFilterMeta = {
  global: { value: null, matchMode: FilterMatchMode.CONTAINS },
  PackagingDataId: {
    operator: FilterOperator.AND,
    constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }],
  },
  Description: {
    operator: FilterOperator.AND,
    constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }],
  },
  PackagingMaterial: { value: null, matchMode: FilterMatchMode.EQUALS },
  'TareWeight.Value': {
    operator: FilterOperator.AND,
    constraints: [{ value: null, matchMode: FilterMatchMode.EQUALS }],
  },
  'TareWeight.Unit': {
    operator: FilterOperator.AND,
    constraints: [{ value: null, matchMode: FilterMatchMode.EQUALS }],
  },
  PackagingIdentifier: {
    operator: FilterOperator.AND,
    constraints: [{ value: null, matchMode: FilterMatchMode.EQUALS }],
  },
  PropertyIdentifier: { value: null, matchMode: FilterMatchMode.EQUALS },
  IsStackable: { value: null, matchMode: FilterMatchMode.EQUALS },
}

type Props = {
  config: TableConfig
  formik: FormikProps<FormValues>
}

export const PackagingMaterialTable: React.FC<Props> = ({ config, formik }) => {
  const { t } = useTranslation()
  const { data, isLoading } = useAllPackagingMaterial()

  const [filters, setFilters] = useState<DataTableFilterMeta>(defaultFilters)
  const [globalFilterValue, setGlobalFilterValue] = useState<string>('')

  useEffect(() => {
    initFilters()
  }, [])

  const clearFilter = () => {
    initFilters()
  }

  const onGlobalFilterChange = (value: string) => {
    let _filters = JSON.parse(JSON.stringify(filters))

    // @ts-ignore
    _filters['global'].value = value
    setFilters(_filters)
    setGlobalFilterValue(value)
  }

  const initFilters = () => {
    setFilters(defaultFilters)
    setGlobalFilterValue('')
  }

  const headerTitle = t(
    'deliveryForecastDespatchAdvice.shippingDetails.packagingTable.add.dialog.packagingMaterial.table.title',
  )
  const headerActions = (
    <SearchField
      className="max-w-30rem w-full ml-auto"
      classNameInput="surface-50"
      searchValue={globalFilterValue}
      setSearchValue={onGlobalFilterChange}
      placeholder={t(
        'deliveryForecastDespatchAdvice.shippingDetails.packagingTable.add.dialog.packagingMaterial.table.search.placeholder',
      )}
    />
  )

  return (
    <>
      {formik.errors.packagingData && (
        <Message
          severity="error"
          text={formik.errors.packagingData}
          className="mb-3"
        />
      )}

      <ConfigurableTable
        config={config}
        data={data}
        headerTitle={headerTitle}
        headerActions={headerActions}
        defaultFilters={filters}
        updateDefaultFilters={setFilters}
        clearDefaultFilter={clearFilter}
        globalFilterFields={['PackagingDataId', 'Description']}
        setGlobalFilterValue={setGlobalFilterValue}
        isLoading={isLoading}
        dataKey="Id"
        stateKey=""
        selected={formik.values.packagingData}
        setSelected={(rowData: any) => {
          formik.setFieldValue('packagingData', rowData)
        }}
      >
        <Column
          selectionMode="single"
          headerStyle={{ width: '3rem' }}
          frozen
        ></Column>
        <Column field="PackagingDataId" dataType="text" filter></Column>
        <Column field="Description"></Column>
        <Column
          field="PackagingMaterial"
          dataType="text"
          body={(data: DeliveryForecastDespatchAdvicePackagingMaterial) =>
            PackagingTypeBodyTemplate(data.PackagingMaterial)
          }
          filterElement={PackagingTypeFilterTemplate}
          filter={true}
          showFilterMatchModes={false}
        ></Column>
        <Column
          field="TareWeight.Value"
          dataType="numeric"
          body={(data: DeliveryForecastDespatchAdvicePackagingMaterial) =>
            DecimalBodyTemplate(data.TareWeight?.Value || 0)
          }
          filterElement={DecimalFilterTemplate}
        ></Column>
        <Column
          field="TareWeight.Unit"
          dataType="text"
          body={(data: DeliveryForecastDespatchAdvicePackagingMaterial) =>
            WeightUnitBodyTemplate(data.TareWeight?.Unit)
          }
          showFilterMatchModes={false}
        ></Column>
        <Column
          field="PackagingIdentifier"
          dataType="text"
          body={(data: DeliveryForecastDespatchAdvicePackagingMaterial) =>
            PackagingIdentifierBodyTemplate(data.PackagingIdentifier)
          }
          filterElement={PackagingIdentifierFilterTemplate}
          showFilterMatchModes={false}
        ></Column>
        <Column
          field="PropertyIdentifier"
          dataType="text"
          body={(data: DeliveryForecastDespatchAdvicePackagingMaterial) =>
            PropertyIdentifierBodyTemplate(data.PropertyIdentifier)
          }
          filterElement={PropertyIdentifierFilterTemplate}
          showFilterMatchModes={false}
        ></Column>
        <Column
          field="IsStackable"
          dataType="text"
          body={(data: DeliveryForecastDespatchAdvicePackagingMaterial) =>
            BooleanBodyTemplate(data.IsStackable)
          }
          filterElement={BooleanFilterTemplate}
          showFilterMatchModes={false}
          filterMatchMode={FilterMatchMode.EQUALS}
        ></Column>
      </ConfigurableTable>
    </>
  )
}
